import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, CheckCircle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { formatCurrency } from '@/utils/currency';
import type { Payment } from '../../types/payment.types';
import { StatusBadge } from '@/components/status/StatusBadge';

export interface PaymentsTableProps {
  payments: Payment[];
  isLoading: boolean;
  error: Error | null;
  selectedPayments: string[];
  markingPaidIds: string[];
  userCountry: string;
  onSelectPayment: (paymentId: string) => void;
  onSelectAll: () => void;
  onMarkPaid: (paymentId: string) => void;
  onViewPayment: (payment: Payment) => void;
}

export const PaymentsTable: React.FC<PaymentsTableProps> = ({
  payments,
  isLoading,
  error,
  selectedPayments,
  markingPaidIds,
  userCountry,
  onSelectPayment,
  onSelectAll,
  onMarkPaid,
  onViewPayment,
}) => {
  return (
    <div className="bg-white rounded-lg border">
      {isLoading ? (
        <div className="p-6 text-center text-gray-500">Loading payments...</div>
      ) : error ? (
        <div className="p-6 text-center text-red-500">Error loading payments: {error.message}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedPayments.length === payments.length && payments.length > 0}
                  onCheckedChange={onSelectAll}
                />
              </TableHead>
              <TableHead>Influencer</TableHead>
              <TableHead>Campaign</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Base Fee
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>Contracted base fee before taxes</TooltipContent>
                  </Tooltip>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  GST
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>GST amount (added to base fee)</TooltipContent>
                  </Tooltip>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  TDS
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>TDS amount (deducted from base fee)</TooltipContent>
                  </Tooltip>
                </div>
              </TableHead>
              <TableHead>Net Payable</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="text-center py-8 text-gray-500">
                  No payments found
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <TableRow
                  key={payment.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => onViewPayment(payment)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedPayments.includes(payment.id)}
                      onCheckedChange={() => onSelectPayment(payment.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={payment.influencer?.profilePic ?? undefined} />
                        <AvatarFallback>{payment.influencer?.avatar || 'IN'}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-gray-900">{payment.influencer?.name}</p>
                        <p className="text-xs text-gray-500">ID: {payment.id.slice(0, 8)}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-900">{payment.campaign}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium">
                      {formatCurrency(payment.baseFee, userCountry)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">
                      {payment.gstApplied
                        ? formatCurrency(payment.gstAmount, userCountry)
                        : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">
                      {payment.tdsDeducted
                        ? formatCurrency(payment.tdsAmount, userCountry)
                        : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(payment.netPayable, userCountry)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={payment.status} />
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">
                      {payment.dueDate
                        ? new Date(payment.dueDate).toLocaleDateString()
                        : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600 capitalize">
                      {payment.paymentMode ? payment.paymentMode.replace('_', ' ') : '-'}
                    </span>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {payment.hasInvoice && payment.invoiceUrl ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 text-blue-600"
                        asChild
                      >
                        <a
                          href={payment.invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          View
                        </a>
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">No invoice</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end space-x-2">
                      {payment.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onMarkPaid(payment.id)}
                          disabled={markingPaidIds.includes(payment.id)}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {markingPaidIds.includes(payment.id) ? 'Updating...' : 'Mark Paid'}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onViewPayment(payment)}
                      >
                        Details
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default PaymentsTable;
